import { useCallback, useEffect, useState } from "react";
import { Alert, App, Button, Descriptions, Form, Input, Modal, Result, Select, Space, Table, Tabs, Tooltip } from "antd";
import {
  approveFreeze,
  approveRehang,
  approveVoid,
  createRehang,
  freezeUnit,
  inspectReturn,
  listBatches,
  listExceptions,
  listLocations,
  markReturned,
  noteForeign,
  noteMissing,
  rejectVoid,
  requestFreeze,
  unfreezeUnit,
  unitsByTracking,
  type BatchRow,
  type BlockRow,
  type FreezeRow,
  type LocationCard,
  type RehangRow,
  type ReturnInspect,
  type VoidRow,
} from "../api/client";
import type { ItemStatus } from "@shoe-trace/shared";
import { AdminScanNotice, formatWhen, LoadFail, PageSpin, PasswordModal, requestStatusLabel, ScanBox, StatusTag } from "../components/widgets";
import { canManage, useSession } from "../session";

interface ReturnRow {
  serial: string;
  humanCode: string;
  status: ItemStatus;
  locationCode: string | null;
  shippedAt: string | null;
}

export function Exceptions(): JSX.Element {
  const { message } = App.useApp();
  const { user } = useSession();
  const manager = canManage(user);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [voids, setVoids] = useState<VoidRow[]>([]);
  const [freezes, setFreezes] = useState<FreezeRow[]>([]);
  const [rehangs, setRehangs] = useState<RehangRow[]>([]);
  const [blocks, setBlocks] = useState<BlockRow[]>([]);
  const [password, setPassword] = useState<null | { kind: "void" | "rehang" | "freeze" | "unfreeze" | "freeze-request"; id: string }>(null);
  const [rejectOpen, setRejectOpen] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [freezeSerial, setFreezeSerial] = useState("");
  const [freezeReason, setFreezeReason] = useState("质检不合格");
  const [freezeNote, setFreezeNote] = useState("");
  const [rehangProfile, setRehangProfile] = useState<string | null>(null);
  const [locations, setLocations] = useState<LocationCard[]>([]);
  const [batches, setBatches] = useState<BatchRow[]>([]);
  const [lookupLocation, setLookupLocation] = useState<string | undefined>(undefined);
  const [lookupBatch, setLookupBatch] = useState<string | undefined>(undefined);
  const [missingNote, setMissingNote] = useState("");
  const [returned, setReturned] = useState<ReturnInspect | null>(null);
  const [tracking, setTracking] = useState("");
  const [trackingRows, setTrackingRows] = useState<ReturnRow[]>([]);
  const [foreignNote, setForeignNote] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    void Promise.all([listExceptions(), listLocations()])
      .then(([data, locationRows]) => {
        setVoids(data.voids);
        setFreezes(data.freezes);
        setRehangs(data.rehangs);
        setBlocks(data.blocks);
        setLocations(locationRows.rows);
        setFailed(false);
      })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && voids.length === 0 && blocks.length === 0) return <PageSpin tip="正在读取待处理的异常…" />;
  if (failed) return <LoadFail title="这一步没有完成。" onRetry={load} />;

  function own(applicantId: string): boolean {
    return applicantId === user?.id;
  }

  return (
    <>
      <AdminScanNotice />
      <Tabs
        items={[
          {
            key: "void",
            label: "作废审批",
            children: (
              <Table
                rowKey="requestNo"
                dataSource={voids}
                locale={{ emptyText: "这一类里没有待处理的事。" }}
                columns={[
                  { title: "时间", dataIndex: "createdAt", render: (value: string) => formatWhen(value) },
                  { title: "单号", dataIndex: "requestNo" },
                  { title: "序列号", dataIndex: "serial" },
                  { title: "申请人", dataIndex: "applicantName" },
                  { title: "原因", dataIndex: "reason" },
                  { title: "新号", dataIndex: "newSerial", render: (value: string | null) => value ?? "—" },
                  { title: "状态", dataIndex: "status", render: (value: string) => requestStatusLabel(value) },
                  {
                    title: "操作",
                    render: (_value: unknown, row: VoidRow) => manager && row.status === "PENDING" ? (
                      <Space>
                        <Tooltip title={own(row.applicantId) ? "这张申请是你提交的，要换主管账号来通过。" : ""}>
                          <Button disabled={own(row.applicantId)} onClick={() => setPassword({ kind: "void", id: row.id })}>通过并作废这个号</Button>
                        </Tooltip>
                        <Button onClick={() => setRejectOpen(row.id)}>退回这张申请</Button>
                      </Space>
                    ) : "—",
                  },
                ]}
              />
            ),
          },
          {
            key: "block",
            label: "拦截与放行",
            children: (
              <>
                <Alert type="info" showIcon message="这里只查看被拦住的记录。本期不在这一页跳过更早批次，请回到出库复核改拣更早的那批。" />
                <Table
                  rowKey="id"
                  dataSource={blocks}
                  locale={{ emptyText: "还没有拦截。出库复核拦住不该发的批次时，会出现在这里。" }}
                  columns={[
                    { title: "时间", dataIndex: "createdAt", render: (value: string) => formatWhen(value) },
                    { title: "出库单", dataIndex: "orderNo" },
                    { title: "扫到的号", dataIndex: "serial" },
                    { title: "说明", dataIndex: "reason" },
                    { title: "操作人", dataIndex: "operatorName" },
                    { title: "放行数", dataIndex: "releasedQty" },
                  ]}
                />
              </>
            ),
          },
          {
            key: "freeze",
            label: "冻结",
            children: (
              <>
                <Form layout="vertical" style={{ maxWidth: 480 }}>
                  <Form.Item label="序列号">
                    <Input value={freezeSerial} onChange={(event) => setFreezeSerial(event.target.value)} placeholder="扫到的 8 位序列号" />
                  </Form.Item>
                  <Form.Item label="原因">
                    <Select value={freezeReason} onChange={setFreezeReason} options={[{ value: "质检不合格" }, { value: "退货隔离" }, { value: "其他" }]} />
                  </Form.Item>
                  <Form.Item label="补充说明">
                    <Input.TextArea value={freezeNote} onChange={(event) => setFreezeNote(event.target.value)} />
                  </Form.Item>
                  {manager ? (
                    <Space>
                      <Button type="primary" onClick={() => setPassword({ kind: "freeze", id: freezeSerial })}>冻结这双并挡住出库</Button>
                      <Button onClick={() => setPassword({ kind: "unfreeze", id: freezeSerial })}>解除冻结</Button>
                    </Space>
                  ) : (
                    <Button
                      onClick={() => {
                        void requestFreeze(freezeSerial, freezeReason, freezeNote)
                          .then(() => {
                            message.success("冻结申请已交给主管。在通过之前，这双仍按原状态处理。");
                            load();
                          })
                          .catch((error: unknown) => message.error(error instanceof Error ? error.message : "这一步没有完成。"));
                      }}
                    >
                      提交冻结申请
                    </Button>
                  )}
                </Form>
                <Table
                  style={{ marginTop: 16 }}
                  rowKey="requestNo"
                  dataSource={freezes}
                  locale={{ emptyText: "这一类里没有待处理的事。" }}
                  columns={[
                    { title: "时间", dataIndex: "createdAt", render: (value: string) => formatWhen(value) },
                    { title: "序列号", dataIndex: "serial" },
                    { title: "申请人", dataIndex: "applicantName" },
                    { title: "原因", dataIndex: "reason" },
                    { title: "状态", dataIndex: "status", render: (value: string) => requestStatusLabel(value) },
                    {
                      title: "操作",
                      render: (_v: unknown, row: FreezeRow) => manager && row.status === "PENDING" ? (
                        <Button disabled={own(row.applicantId)} onClick={() => setPassword({ kind: "freeze-request", id: row.id })}>通过并冻结</Button>
                      ) : "—",
                    },
                  ]}
                />
              </>
            ),
          },
          {
            key: "rehang",
            label: "脱落补挂",
            children: (
              <>
                <ScanBox
                  placeholder="扫描鞋盒上还在的外标"
                  onSubmit={async (code) => {
                    try {
                      const created = await createRehang({ scannedCode: code });
                      setRehangProfile(created.id);
                      message.success(manager ? "已提交补挂申请，请再确认本人后生成新号。" : "补挂申请已交给主管。");
                      load();
                    } catch (error: unknown) {
                      message.error(error instanceof Error ? error.message : "这张外标没有扫出来。可以改用库位和批次查找。");
                    }
                  }}
                />
                {rehangProfile ? <Descriptions items={[{ key: "id", label: "申请", children: rehangProfile }]} /> : null}
                <Space>
                  <Select
                    showSearch
                    filterOption={false}
                    style={{ width: 180 }}
                    placeholder="输入库位编码"
                    value={lookupLocation}
                    onSearch={(text) => {
                      const query = text.trim().toUpperCase();
                      if (!query) return;
                      void listLocations({ q: query }).then((page) => setLocations(page.rows));
                    }}
                    onChange={setLookupLocation}
                    options={locations.map((row) => ({ value: row.code, label: row.code }))}
                  />
                  <Select
                    showSearch
                    filterOption={false}
                    style={{ width: 220 }}
                    placeholder="搜索批次号"
                    value={lookupBatch}
                    onSearch={(text) => {
                      const query = text.trim();
                      if (!query) {
                        setBatches([]);
                        return;
                      }
                      void listBatches(query, 1, 20).then((page) => setBatches(page.rows));
                    }}
                    onChange={setLookupBatch}
                    options={batches.map((row) => ({ value: row.id, label: row.batchNo }))}
                  />
                  <Button
                    onClick={() => {
                      if (!lookupLocation || !lookupBatch) return;
                      void createRehang({ locationCode: lookupLocation, batchId: lookupBatch })
                        .then(() => {
                          message.success("已按库位和批次记下补挂申请。");
                          load();
                        })
                        .catch((error: unknown) => message.error(error instanceof Error ? error.message : "没能对上这双鞋"));
                    }}
                  >
                    按库位和批次查找
                  </Button>
                </Space>
                <Result status="warning" title="没能对上这双鞋" subTitle="不要放进试点库位。退回质检，并写下鞋盒现在放在哪。" />
                <Input.TextArea value={missingNote} onChange={(event) => setMissingNote(event.target.value)} />
                <Button
                  onClick={() => {
                    void noteMissing(missingNote).then(() => message.success("已记下退回质检。"));
                  }}
                >
                  记下退回质检
                </Button>
                <Table
                  style={{ marginTop: 16 }}
                  rowKey="requestNo"
                  dataSource={rehangs}
                  locale={{ emptyText: "这一类里没有待处理的事。" }}
                  columns={[
                    { title: "时间", dataIndex: "createdAt", render: (value: string) => formatWhen(value) },
                    { title: "旧号", dataIndex: "oldSerial" },
                    { title: "新号", dataIndex: "newSerial", render: (value: string | null) => value ?? "—" },
                    { title: "申请人", dataIndex: "applicantName" },
                    { title: "状态", dataIndex: "status", render: (value: string) => requestStatusLabel(value) },
                    {
                      title: "操作",
                      render: (_v: unknown, row: RehangRow) => manager && row.status === "PENDING" ? (
                        <Button disabled={own(row.applicantId)} onClick={() => setPassword({ kind: "rehang", id: row.id })}>批准补挂并生成新号</Button>
                      ) : "—",
                    },
                  ]}
                />
              </>
            ),
          },
          {
            key: "return",
            label: "退货核对",
            children: (
              <>
                <Alert type="info" showIcon message="这里只判断是不是本仓发出，并标记已退回。试点期内不能从这页重新上架。" />
                <ScanBox
                  placeholder="扫描吊牌或外标"
                  onSubmit={async (code) => {
                    setReturned(await inspectReturn(code));
                  }}
                />
                {returned?.kind === "missing" ? (
                  <Result status="warning" title="系统里没有这张吊牌" />
                ) : null}
                {returned?.kind === "found" && returned.status === "SHIPPED" ? (
                  <Result
                    status="success"
                    title="这是本仓发出的"
                    subTitle={`${returned.humanCode ?? ""} ${returned.shippedAt ? formatWhen(returned.shippedAt) : ""} ${returned.trackingNo ?? "还没记下快递单号"}`}
                    extra={returned.serial ? <Button onClick={() => void markReturned(returned.serial ?? "").then(() => message.success("已标记已退回并隔离存放。"))}>标记已退回并隔离存放</Button> : null}
                  />
                ) : null}
                {returned?.kind === "found" && returned.status === "RETURNED" ? <Result status="info" title="这双已经记过退回" /> : null}
                {returned?.kind === "found" && returned.status && returned.status !== "SHIPPED" && returned.status !== "RETURNED" ? (
                  <Result status="warning" title="这双还没有出库记录" subTitle="不能按退货收进来。请核对是不是扫错。" />
                ) : null}
                <Space>
                  <Input placeholder="快递单号" value={tracking} onChange={(event) => setTracking(event.target.value)} />
                  <Button onClick={() => void unitsByTracking(tracking).then(setTrackingRows)}>按快递单号列出这一单的全部鞋</Button>
                </Space>
                <Table<ReturnRow>
                  rowKey="serial"
                  dataSource={trackingRows}
                  columns={[
                    { title: "序列号", dataIndex: "serial" },
                    { title: "SKU", dataIndex: "humanCode" },
                    { title: "状态", dataIndex: "status", render: (status, row) => <StatusTag status={status} locationCode={row.locationCode} /> },
                    { title: "出库时间", dataIndex: "shippedAt", render: (value: string | null) => formatWhen(value) },
                    {
                      title: "操作",
                      render: (_v: unknown, row) => row.status === "SHIPPED" ? (
                        <Button onClick={() => void markReturned(row.serial).then(() => message.success("已标记已退回并隔离存放。"))}>就用这双标记已退回</Button>
                      ) : "—",
                    },
                  ]}
                />
                <Input.TextArea placeholder="非本仓说明" value={foreignNote} onChange={(event) => setForeignNote(event.target.value)} />
                <Button
                  onClick={() => {
                    void noteForeign(foreignNote, tracking).then(() => message.success("已留下说明。这双不要放进试点库位。"));
                  }}
                >
                  记下非本仓留证
                </Button>
              </>
            ),
          },
        ]}
      />
      <Modal
        destroyOnClose
        open={rejectOpen !== null}
        title="退回这张申请"
        okText="把申请退回给作业员"
        cancelText="先不操作"
        onCancel={() => setRejectOpen(null)}
        onOk={() => {
          if (!rejectOpen || !rejectReason.trim()) {
            message.warning("请写下退回原因。");
            return Promise.reject(new Error("empty"));
          }
          return rejectVoid(rejectOpen, rejectReason.trim()).then(() => {
            message.success("已退回给申请人。对方可以改原因后再交。");
            setRejectOpen(null);
            setRejectReason("");
            load();
          });
        }}
      >
        <Input.TextArea value={rejectReason} onChange={(event) => setRejectReason(event.target.value)} placeholder="退回原因" />
      </Modal>
      <PasswordModal
        open={password !== null}
        okText={password?.kind === "void" ? "确认本人并作废这个号" : password?.kind === "rehang" ? "确认本人并生成新号" : "确认本人并冻结这双"}
        onCancel={() => setPassword(null)}
        onConfirm={async (secret) => {
          if (!password) return;
          if (password.kind === "void") {
            const result = await approveVoid(password.id, secret);
            message.success(`这个号已作废，以后不会再用。新号是 ${result.newSerial}。请到赋码打印补打。旧号不能再启用。`);
          } else if (password.kind === "rehang") {
            const result = await approveRehang(password.id, secret);
            message.success(`新号已关联旧号 ${result.oldSerial}。请打印新吊牌挂上。旧号作废，不能再用。`);
          } else if (password.kind === "freeze") {
            await freezeUnit(password.id, freezeReason, secret, freezeNote);
            message.success("这双已冻结。出库复核会拦住它。");
          } else if (password.kind === "unfreeze") {
            await unfreezeUnit(password.id, secret);
            message.success("已解除冻结。库位还在的可以拿去拣，库位空着的先去上架。");
          } else {
            await approveFreeze(password.id, secret);
            message.success("这双已冻结。出库复核会拦住它。");
          }
          load();
        }}
      />
    </>
  );
}
